/**
 * PAILENA — Favoris (repris du thème Effleura, textes au vouvoiement)
 *
 * Les favoris vivent dans le navigateur du visiteur (localStorage).
 * Aucun compte client n'est activé sur la boutique : il n'existe donc aucun
 * endroit côté Shopify où rattacher la liste de quelqu'un. Conséquence
 * assumée : pas de synchronisation entre appareils.
 *
 * On stocke des HANDLES de produit, pas des identifiants numériques :
 * lisibles, stables, et directement utilisables pour /products/<handle>.js
 * afin de reconstruire la liste sans API ni application.
 *
 * Trois éléments personnalisés :
 *   <favoris-coeur data-handle="…">   le bouton cœur (carte, fiche produit)
 *   <favoris-compteur>                la pastille de l'en-tête
 *   <favoris-liste>                   la page « Mes favoris »
 */

const CLE = 'pailena:favoris:v1';
const EVENEMENT = 'pailena:favoris:change';

/* Paramètre du lien de partage, et plafond du nombre de handles qu'on y met.
   Au-delà, l'URL devient trop longue pour être collée de façon fiable dans
   une messagerie. 50 handles ≈ 1 200 caractères, on reste large. */
const PARAM_PARTAGE = 'f';
const MAX_PARTAGE = 50;

/**
 * Un handle vient parfois d'une URL, donc d'une source non maîtrisée : il
 * finit dans un fetch et dans un href. On n'accepte que la forme réellement
 * produite par Shopify. Tout le reste est rejeté, pas échappé.
 */
function handleValide(h) {
  return typeof h === 'string' && /^[a-z0-9][a-z0-9-]{0,120}$/.test(h);
}

/** Suivi CRO : pailena_favorite_add / pailena_favorite_remove (Shopify.analytics + dataLayer), sans donnée personnelle */
function suivre(nom, handle) {
  const donnees = { product_handle: handle };
  try {
    window.Shopify?.analytics?.publish?.(`pailena_${nom}`, donnees);
  } catch {
    /* le suivi ne doit jamais casser la page */
  }
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: `pailena_${nom}`, ...donnees });
}

/* --------------------------------------------------------------------------
   Stockage
   -------------------------------------------------------------------------- */

/**
 * localStorage peut lever une exception : navigation privée sur certains
 * navigateurs, cookies tiers bloqués, quota plein. On bascule alors sur une
 * mémoire de session : les cœurs restent cohérents pendant la visite, la
 * liste disparaît au rechargement. Mieux qu'une interface qui ne réagit plus.
 */
let memoire = null;

function stockageDisponible() {
  if (memoire !== null) return false;
  try {
    const test = '__pailena__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    memoire = memoire || [];
    return false;
  }
}

/** @returns {Array<{handle: string, ajouteLe: number}>} */
export function lireFavoris() {
  if (!stockageDisponible()) return memoire || [];
  try {
    const brut = localStorage.getItem(CLE);
    if (!brut) return [];
    const donnees = JSON.parse(brut);
    if (!Array.isArray(donnees)) return [];
    return donnees.filter((e) => e && handleValide(e.handle));
  } catch {
    /* Contenu corrompu (édition manuelle, ancienne version) : on repart propre
       plutôt que de laisser toute la fonctionnalité en panne. */
    return [];
  }
}

function ecrireFavoris(liste) {
  if (stockageDisponible()) {
    try {
      localStorage.setItem(CLE, JSON.stringify(liste));
    } catch {
      memoire = liste;
    }
  } else {
    memoire = liste;
  }
  document.dispatchEvent(new CustomEvent(EVENEMENT, { detail: { liste } }));
}

export function estFavori(handle) {
  return lireFavoris().some((e) => e.handle === handle);
}

export function compterFavoris() {
  return lireFavoris().length;
}

/**
 * Ajoute ou retire, et renvoie le nouvel état.
 * @returns {boolean} true si le produit est désormais en favori
 */
export function basculerFavori(handle) {
  const liste = lireFavoris();
  const index = liste.findIndex((e) => e.handle === handle);

  if (index >= 0) {
    liste.splice(index, 1);
    ecrireFavoris(liste);
    return false;
  }

  /* Les plus récents en tête : c'est l'ordre attendu sur une page favoris. */
  liste.unshift({ handle, ajouteLe: Date.now() });
  ecrireFavoris(liste);
  return true;
}

export function retirerFavori(handle) {
  const liste = lireFavoris().filter((e) => e.handle !== handle);
  ecrireFavoris(liste);
}

/* --------------------------------------------------------------------------
   Partage par lien

   Sans compte client, c'est le seul moyen pour quelqu'un de retrouver sa
   liste sur un autre appareil — et le seul filet contre l'effacement du
   stockage par Safari, qui purge le localStorage au bout de 7 jours sans
   visite (WebKit ITP, donc aussi tous les navigateurs sur iPhone).
   -------------------------------------------------------------------------- */

/** @returns {{url: string, tronque: boolean}} */
export function construireLienPartage() {
  const handles = lireFavoris().map((e) => e.handle);
  const retenus = handles.slice(0, MAX_PARTAGE);

  const url = new URL(window.location.href);
  url.hash = '';
  url.searchParams.set(PARAM_PARTAGE, retenus.join(','));

  return { url: url.toString(), tronque: handles.length > retenus.length };
}

/**
 * Fusionne les favoris d'un lien reçu avec ceux déjà présents : on ne
 * remplace jamais la liste de quelqu'un par celle d'un lien qu'on lui a
 * envoyé. Puis on nettoie l'URL, sinon un bijou retiré réapparaîtrait au
 * moindre rechargement.
 */
function importerDepuisUrl() {
  let params;
  try {
    params = new URL(window.location.href).searchParams;
  } catch {
    return;
  }

  const brut = params.get(PARAM_PARTAGE);
  if (!brut) return;

  const recus = brut.split(',').map((h) => h.trim().toLowerCase()).filter(handleValide);

  if (recus.length > 0) {
    const liste = lireFavoris();
    const connus = new Set(liste.map((e) => e.handle));
    const nouveaux = recus.filter((h) => !connus.has(h)).map((h) => ({ handle: h, ajouteLe: Date.now() }));
    if (nouveaux.length > 0) ecrireFavoris([...nouveaux, ...liste]);
  }

  try {
    const propre = new URL(window.location.href);
    propre.searchParams.delete(PARAM_PARTAGE);
    history.replaceState({}, '', propre.toString());
  } catch {
    /* Pas d'historique manipulable : le paramètre reste, sans conséquence. */
  }
}

/* Un autre onglet a modifié la liste : on resynchronise l'affichage. */
window.addEventListener('storage', (event) => {
  if (event.key !== CLE) return;
  document.dispatchEvent(new CustomEvent(EVENEMENT, { detail: { liste: lireFavoris() } }));
});

/* --------------------------------------------------------------------------
   <favoris-coeur>
   -------------------------------------------------------------------------- */

class FavorisCoeur extends HTMLElement {
  #bouton = null;

  connectedCallback() {
    this.#bouton = this.querySelector('button');
    if (!this.#bouton) return;

    this.#bouton.addEventListener('click', this.#surClic);
    document.addEventListener(EVENEMENT, this.#rafraichir);
    this.#rafraichir();
  }

  disconnectedCallback() {
    this.#bouton?.removeEventListener('click', this.#surClic);
    document.removeEventListener(EVENEMENT, this.#rafraichir);
  }

  get handle() {
    return this.dataset.handle || '';
  }

  /* Champ-flèche et non méthode : une méthode privée n'est pas réassignable,
     donc impossible à lier avec .bind() — et il faut la même référence de
     fonction pour pouvoir retirer l'écouteur. */
  #surClic = (event) => {
    /* La carte produit entière est un lien étiré. Le thème ignore déjà les
       clics sur un <button>, mais on coupe la propagation par sécurité :
       un clic sur le cœur ne doit jamais ouvrir la fiche produit. */
    event.preventDefault();
    event.stopPropagation();

    if (!this.handle) return;
    const desormaisFavori = basculerFavori(this.handle);
    this.#annoncer(desormaisFavori);
    suivre(desormaisFavori ? 'favorite_add' : 'favorite_remove', this.handle);
  };

  #rafraichir = () => {
    if (!this.#bouton) return;
    const actif = estFavori(this.handle);
    this.#bouton.setAttribute('aria-pressed', actif ? 'true' : 'false');
    this.classList.toggle('favoris-coeur--actif', actif);

    const titre = this.dataset.titre || '';
    this.#bouton.setAttribute(
      'aria-label',
      actif ? `Retirer ${titre} des favoris`.trim() : `Ajouter ${titre} aux favoris`.trim()
    );

    /* Libellé visible, présent sur la fiche produit seulement. Il est rempli
       par Liquid au rendu initial pour éviter un texte vide avant hydratation ;
       on ne fait que le mettre à jour ici. */
    const texte = this.querySelector('[data-favoris-texte]');
    if (texte) {
      texte.textContent = actif
        ? this.dataset.texteActif || 'Dans vos favoris'
        : this.dataset.texteInactif || 'Ajouter aux favoris';
    }
  };

  /* Le changement d'état d'un bouton n'est pas verbalisé spontanément par
     tous les lecteurs d'écran : on l'annonce explicitement. */
  #annoncer(actif) {
    const region = document.getElementById('favoris-annonce');
    if (!region) return;
    const titre = this.dataset.titre || 'Le bijou';
    region.textContent = actif ? `${titre} ajouté aux favoris.` : `${titre} retiré des favoris.`;
  }
}

/* --------------------------------------------------------------------------
   <favoris-compteur>
   -------------------------------------------------------------------------- */

class FavorisCompteur extends HTMLElement {
  connectedCallback() {
    document.addEventListener(EVENEMENT, this.#rafraichir);
    this.#rafraichir();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENEMENT, this.#rafraichir);
  }

  #rafraichir = () => {
    const total = compterFavoris();
    const texte = this.querySelector('[data-compteur-texte]');
    if (texte) texte.textContent = total > 99 ? '99+' : String(total);
    this.toggleAttribute('hidden', total === 0);
  };
}

/* --------------------------------------------------------------------------
   <favoris-liste>  — page « Mes favoris »
   -------------------------------------------------------------------------- */

class FavorisListe extends HTMLElement {
  /* Garde-fou de ré-entrance. #rendre() peut élaguer les produits disparus,
     donc écrire dans le stockage, donc réémettre l'événement de changement,
     donc relancer #rendre() — deux rendus concurrents écriraient dans la
     même grille et pourraient dupliquer des cartes. */
  #rendEnCours = false;

  /* Un changement survenu PENDANT un rendu ne doit pas être perdu : deux
     clics rapprochés déclenchent un rendu sur le premier, et le second
     arrivait alors que le garde-fou était levé. Il était simplement ignoré,
     et le bijou n'apparaissait jamais. On note qu'il faudra recommencer. */
  #rendreDeNouveau = false;

  connectedCallback() {
    document.addEventListener(EVENEMENT, this.#surChangement);
    this.#rendre();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENEMENT, this.#surChangement);
  }

  /* Un retrait depuis cette page n'a pas besoin d'un rendu complet : on
     enlève la carte, sans refaire les appels réseau.
     Un AJOUT, lui, en a besoin. Il arrive quand un autre onglet modifie la
     liste : sans ce cas, la page masquait l'état vide sans afficher la
     moindre carte — grille vide et aucun message. */
  #surChangement = () => {
    if (this.#rendEnCours) {
      this.#rendreDeNouveau = true;
      return;
    }

    const handles = new Set(lireFavoris().map((e) => e.handle));
    const rendus = new Set([...this.querySelectorAll('[data-handle]')].map((n) => n.dataset.handle));

    const ajouts = [...handles].some((h) => !rendus.has(h));
    if (ajouts) {
      this.#rendre();
      return;
    }

    for (const carte of this.querySelectorAll('[data-handle]')) {
      if (!handles.has(carte.dataset.handle)) carte.remove();
    }
    this.#basculerVide(handles.size === 0);
    /* Le total doit suivre ce chemin rapide aussi : sans cette ligne, la page
       affichait « 3 bijoux » au-dessus de deux cartes après un retrait. */
    this.#majTotal(this.querySelectorAll('[data-handle]').length);
  };

  /* Une seule source de vérité pour le libellé, appelée par le rendu complet
     comme par le retrait direct. */
  #majTotal(nombre) {
    const total = this.querySelector('[data-favoris-total]');
    if (!total) return;
    total.textContent = nombre === 1 ? '1 bijou' : `${nombre} bijoux`;
  }

  #basculerVide(vide) {
    const zoneVide = this.querySelector('[data-favoris-vide]');
    const grille = this.querySelector('[data-favoris-grille]');
    const entete = this.querySelector('[data-favoris-entete]');
    if (zoneVide) zoneVide.toggleAttribute('hidden', !vide);
    if (grille) grille.toggleAttribute('hidden', vide);
    if (entete) entete.toggleAttribute('hidden', vide);
  }

  async #rendre() {
    const grille = this.querySelector('[data-favoris-grille]');
    const chargement = this.querySelector('[data-favoris-chargement]');
    if (!grille || this.#rendEnCours) return;

    this.#rendEnCours = true;
    try {
      /* Tant qu'un changement est arrivé en cours de route, on refait un
         tour. La boucle se termine : chaque passe repart de l'état courant
         du stockage, et #rendreDeNouveau n'est remis à vrai que par un
         changement réel. */
      do {
        this.#rendreDeNouveau = false;
        await this.#rendreVraiment(grille, chargement);
      } while (this.#rendreDeNouveau);
    } finally {
      this.#rendEnCours = false;
    }
  }

  async #rendreVraiment(grille, chargement) {
    const favoris = lireFavoris();
    if (favoris.length === 0) {
      chargement?.setAttribute('hidden', '');
      this.#viderAttente();
      this.#basculerVide(true);
      return;
    }

    const produits = await Promise.all(favoris.map((e) => this.#chargerProduit(e.handle)));

    /* Un produit supprimé ou dépublié renvoie null : on nettoie le stockage
       au lieu d'afficher une carte vide ou un lien mort. */
    const introuvables = favoris.filter((e, i) => produits[i] === null).map((e) => e.handle);
    if (introuvables.length > 0) {
      ecrireFavoris(lireFavoris().filter((e) => !introuvables.includes(e.handle)));
    }

    const valides = produits.filter(Boolean);
    chargement?.setAttribute('hidden', '');

    if (valides.length === 0) {
      this.#viderAttente();
      this.#basculerVide(true);
      return;
    }

    /* Remplace aussi les cartes d'attente posées par le script en ligne de la section. */
    grille.innerHTML = '';
    for (const produit of valides) grille.appendChild(this.#carte(produit));
    grille.removeAttribute('aria-busy');
    this.#basculerVide(false);
    this.#majTotal(valides.length);
  }

  #viderAttente() {
    for (const carte of this.querySelectorAll('.favoris-carte--attente')) carte.remove();
    this.querySelector('[data-favoris-grille]')?.removeAttribute('aria-busy');
  }

  async #chargerProduit(handle) {
    /* Deuxième barrière après handleValide : même si un handle douteux
       franchissait le filtre, il ne peut pas s'échapper du chemin. */
    if (!handleValide(handle)) return null;
    try {
      const reponse = await fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(handle)}.js`);
      if (!reponse.ok) return null;
      return await reponse.json();
    } catch {
      return null;
    }
  }

  #carte(produit) {
    const article = document.createElement('article');
    article.className = 'pl-card favoris-carte';
    article.dataset.handle = produit.handle;
    const racine = window.Shopify?.routes?.root || '/';

    const image = produit.featured_image
      ? `<img src="${this.#redimensionner(produit.featured_image, 600)}" alt="" width="600" height="600" loading="lazy" class="pl-card__image">`
      : '<span class="pl-card__image favoris-carte__vide"></span>';
    const type = (produit.type || '').replace(' anti-stress', '');

    article.innerHTML = `
      <a class="pl-card__link" href="${racine}products/${encodeURIComponent(produit.handle)}">
        <div class="pl-card__media">${image}</div>
        ${type ? `<p class="pl-card__type">${this.#echapper(type)}</p>` : ''}
        <h2 class="pl-card__name">${this.#echapper(produit.title)}</h2>
        <p class="pl-card__price">${produit.price_varies ? 'Dès ' : ''}${this.#prix(produit.price)}</p>
      </a>
      <button type="button" class="favoris-carte__retirer" data-retirer>Retirer<span class="pl-visually-hidden"> ${this.#echapper(produit.title)} des favoris</span></button>
    `;

    article.querySelector('[data-retirer]').addEventListener('click', () => {
      retirerFavori(produit.handle);
      suivre('favorite_remove', produit.handle);
    });

    return article;
  }

  /* Le CDN Shopify redimensionne via le paramètre ?width=, pas en bricolant
     le nom de fichier : c'est la méthode documentée, et elle ne dépend ni de
     l'extension ni de la présence d'un ?v=… dans l'URL.
     Sans ça, une grille de favoris téléchargerait les originaux pleine
     résolution. */
  #redimensionner(url, largeur) {
    if (typeof url !== 'string' || url === '') return url;

    /* On ne réécrit que les trois formes d'URL que Shopify produit. Toute
       autre forme — data:, blob:, une URL déjà transformée par une app —
       est renvoyée intacte : y injecter un paramètre la casserait. */
    const reecrivable = url.startsWith('//') || /^https?:/i.test(url) || url.startsWith('/');
    if (!reecrivable) return url;

    try {
      const a = new URL(url, window.location.origin);
      a.searchParams.set('width', String(largeur));

      /* Trois formes possibles, et il faut rendre la même qu'en entrée :
         //cdn.shopify.com/…   protocol-relative, la forme habituelle de Shopify
         https://cdn…/…        absolue
         /files/…              relative au domaine */
      if (url.startsWith('//')) return `//${a.host}${a.pathname}${a.search}`;
      if (/^https?:/i.test(url)) return a.href;
      return a.pathname + a.search;
    } catch {
      return url;
    }
  }

  #prix(centimes) {
    const devise = window.Shopify?.currency?.active || 'EUR';
    const langue = (window.Shopify?.locale || 'fr').replace('_', '-');
    try {
      return new Intl.NumberFormat(langue, { style: 'currency', currency: devise }).format(centimes / 100).replace(/\s(?=€)/u, '');
    } catch {
      return `${(centimes / 100).toFixed(2)} €`;
    }
  }

  #echapper(texte) {
    const noeud = document.createElement('span');
    noeud.textContent = texte;
    return noeud.innerHTML;
  }
}

/* --------------------------------------------------------------------------
   <favoris-partage>
   -------------------------------------------------------------------------- */

class FavorisPartage extends HTMLElement {
  #bouton = null;
  #retour = null;
  #champ = null;
  #minuteur = 0;

  connectedCallback() {
    this.#bouton = this.querySelector('[data-partage-bouton]');
    this.#retour = this.querySelector('[data-partage-retour]');
    this.#champ = this.querySelector('[data-partage-champ]');
    if (!this.#bouton) return;

    this.#bouton.addEventListener('click', this.#surClic);
    document.addEventListener(EVENEMENT, this.#rafraichir);
    this.#rafraichir();
  }

  disconnectedCallback() {
    this.#bouton?.removeEventListener('click', this.#surClic);
    document.removeEventListener(EVENEMENT, this.#rafraichir);
    clearTimeout(this.#minuteur);
  }

  /* Rien à partager quand la liste est vide. */
  #rafraichir = () => {
    this.toggleAttribute('hidden', compterFavoris() === 0);
  };

  #surClic = async () => {
    const { url, tronque } = construireLienPartage();

    /* L'API presse-papiers exige HTTPS et un geste utilisateur. Les deux sont
       réunis ici, mais elle peut malgré tout être refusée (permission, vieux
       navigateur) : dans ce cas on affiche le lien pour un copier manuel
       plutôt que de laisser croire à une réussite. */
    try {
      if (!navigator.clipboard) throw new Error('indisponible');
      await navigator.clipboard.writeText(url);
      this.#annoncer(tronque ? `Lien copié — les ${MAX_PARTAGE} plus récents.` : 'Lien copié.');
    } catch {
      this.#afficherPourCopieManuelle(url);
    }
  };

  #annoncer(message) {
    if (!this.#retour) return;
    this.#retour.textContent = message;
    this.#retour.hidden = false;
    clearTimeout(this.#minuteur);
    this.#minuteur = setTimeout(() => {
      this.#retour.hidden = true;
    }, 4000);
  }

  #afficherPourCopieManuelle(url) {
    if (!this.#champ) return;
    this.#champ.value = url;
    this.#champ.hidden = false;
    this.#champ.focus();
    this.#champ.select();
    this.#annoncer('Copie automatique refusée par le navigateur : copiez le lien ci-dessous.');
  }
}

/* L'import doit précéder la définition des éléments : quand <favoris-liste>
   est mis à niveau, il lit le stockage immédiatement. Si on importait après,
   la page s'afficherait vide avant de se remplir. */
importerDepuisUrl();

if (!customElements.get('favoris-partage')) customElements.define('favoris-partage', FavorisPartage);
if (!customElements.get('favoris-coeur')) customElements.define('favoris-coeur', FavorisCoeur);
if (!customElements.get('favoris-compteur')) customElements.define('favoris-compteur', FavorisCompteur);
if (!customElements.get('favoris-liste')) customElements.define('favoris-liste', FavorisListe);
